import { Link } from '@mui/material';
import type { NextPageWithAuthOptions } from '@side/next-auth';
import { signIn, useSession } from '@side/next-auth/react';
import { toast } from '@side/pantry';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { LoadingFallback } from '../src/components/LoadingFallback';
// Local copy of getIsEmbedded (not imported from @side/next-auth) so this
// in-progress embed work doesn't bump the shared lib and redeploy every UI.
// TODO: import from @side/next-auth/server once embed testing is complete.
import { AnalyticsEvent, useTrackEvent } from '../src/modules/analytics';
import { getServerFeatureFlag } from '../src/modules/launchdarkly/server';
import { embedDebugLog } from '../src/utils/embed-debug-log';
import { getIsEmbedded } from '../src/utils/is-embedded';
import { sanitizeDestination } from '../src/utils/sanitize-destination';
import { EmbeddedLogin } from '../src/views/embedded-login/EmbeddedLogin';
import { OktaLogin } from '../src/views/okta-login';

/** Destinations under admin-ui are gated by Google IAP and need the popup flow. */
const isIapDestination = (destination?: string) =>
  !!destination && destination.startsWith('/admin');

type LoginPageProps = {
  /**
   * Whether we're rendering inside the Zendesk embed iframe. True only when
   * BOTH server-side signals hold:
   *   - the request is served from an embedded host (embedded.sideinc.com,
   *     embedded-stage.sideinc.dev, *-embedded.<env>-preview-lb.sideinc.dev, …),
   *     via the x-side-origin header the load balancer sets — the same signal
   *     next-auth uses to relax the session cookie's SameSite; and
   *   - the document is framed (`Sec-Fetch-Dest: iframe`), which excludes the
   *     top-level auth popup. The popup lands back on /login on the same
   *     embedded host, so the host check alone would wrongly re-trigger the
   *     popup flow there and deadlock it.
   */
  embedded: boolean;
  /**
   * LaunchDarkly `zendeskEmbedPopupAuth` gate, resolved server-side so the popup
   * UI ships dormant and can be flipped on in a real IAP environment. Only
   * evaluated when `embedded` (normal logins skip it). Resolving it on the
   * server avoids a client race where the authenticated→/redirect effect could
   * fire before a client-side flag loads.
   */
  popupAuthEnabled: boolean;
};

const defaultErrorMessage = (
  <>
    We were not able to log you in. Please try again. If you&apos;re still
    unable to login, please{' '}
    <Link href="mailto:help@sideinc.com?subject=Assistance%20required%20with%20logging%20in%20to%20the%20Side%20App">
      contact Side support.
    </Link>
  </>
);

const LoginPage: NextPageWithAuthOptions<LoginPageProps> = ({
  embedded,
  popupAuthEnabled,
}) => {
  const trackEvent = useTrackEvent();
  const { status } = useSession();
  const [loading, setLoading] = useState(false);

  const { query, push } = useRouter();
  const { logout, reason } = query;
  const logoutStatus = Array.isArray(logout)
    ? logout[0]
    : (logout ?? 'unknown');
  const hasExpiredSession = logoutStatus === 'success' && reason === 'expired';

  // The Zendesk embed loads /login?destination=<admin path> from an embedded
  // host. When the destination is IAP-gated we can't authenticate in-frame (IAP
  // + Okta logins refuse to be framed), so we hand off to a top-level popup.
  // See EmbeddedLogin.
  // Sanitized so a login-pointing/nested/over-long destination can't drive the
  // EmbeddedLogin navigation into the self-wrapping redirect loop.
  const destination = sanitizeDestination(query.destination);
  // Gated behind the `zendeskEmbedPopupAuth` flag (resolved server-side) so the
  // popup UI is dormant until enabled. Flag off ⇒ identical to the prior /login.
  const useEmbeddedPopupAuth =
    popupAuthEnabled && embedded && isIapDestination(destination);

  useEffect(() => {
    // next-auth uses the query param
    // to indicate an error occurred
    // https://next-auth.js.org/configuration/pages#sign-in-page

    if (query.error) {
      // biome-ignore lint/suspicious/noConsole: come back to this
      console.warn('Error on Okta login redirect', { error: query.error });
      trackEvent(AnalyticsEvent.OktaLoginFailure);
      toast('error', defaultErrorMessage);
    }
  }, [query.error, trackEvent]);

  useEffect(() => {
    // In the embedded popup flow we must NOT auto-redirect to an IAP-gated
    // destination in-frame — it would be blocked until the popup establishes the
    // IAP cookie. EmbeddedLogin drives navigation after the popup succeeds.
    if (useEmbeddedPopupAuth) {
      return;
    }
    if (!loading && status === 'authenticated') {
      // if the user is already logged in, redirect them to the app
      const params = new URLSearchParams(window.location.search);
      push(`/redirect?${params.toString()}`).catch((error) => {
        // workaround for https://github.com/vercel/next.js/issues/37362
        if (!error.cancelled) {
          throw error;
        }
      });
    }
  }, [status, push, loading, useEmbeddedPopupAuth]);

  useEffect(() => {
    // The Pendo agent is loaded asynchronously by Segment, so it usually
    // isn't on the window yet when this page mounts — poll until it is,
    // then stop guides from displaying over the login screen. stopGuides()
    // can throw while the agent is still mid-bootstrap; swallow it so it
    // can't blank the page via the ErrorBoundary — guides settle on their own.
    const stopPendoGuides = () => {
      try {
        window.pendo?.stopGuides?.();
      } catch {
        // Agent not fully initialized yet.
      }
    };
    if (window.pendo?.stopGuides) {
      stopPendoGuides();
      return undefined;
    }
    const interval = window.setInterval(() => {
      if (window.pendo?.stopGuides) {
        stopPendoGuides();
        window.clearInterval(interval);
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, []);

  const handleLogin = async () => {
    try {
      trackEvent(AnalyticsEvent.OktaLoginClick);
      signIn('okta', {
        callbackUrl: `${window.location.origin}/identity/login${window.location.search}`,
      });
      setLoading(true);
    } catch (error) {
      // TODO: [PLAT-1731] use standardized logger module in ui
      // biome-ignore lint/suspicious/noConsole: come back to this
      console.warn('Error logging in', { error });
      trackEvent(AnalyticsEvent.OktaLoginFailure);
      toast('error', defaultErrorMessage);
    }
  };

  // Embedded Zendesk flow: hand auth off to a top-level popup regardless of
  // session status, since we can't tell from here whether the IAP cookie is
  // present (it's HttpOnly and cross-site).
  if (useEmbeddedPopupAuth && destination) {
    return <EmbeddedLogin destination={destination} />;
  }

  // they will be redirected with the useEffect above,
  // show a loading state while we wait for the new page
  if (status === 'authenticated') return <LoadingFallback />;

  return (
    <OktaLogin
      handleLogin={handleLogin}
      loading={loading}
      logoutStatus={logoutStatus}
      hasExpiredSession={hasExpiredSession}
    />
  );
};

LoginPage.auth = {
  isPublic: true,
};

/**
 * Count how many times a destination value nests another /identity/login
 * redirect. Depth > 0 means a redirect loop is (re)forming — logged so the
 * loop is visible in Datadog long before it floods.
 */
function getLoginNestingDepth(rawDestination: string | string[] | undefined) {
  let value = Array.isArray(rawDestination)
    ? (rawDestination[0] ?? '')
    : (rawDestination ?? '');
  let depth = 0;
  // Cap iterations defensively; real URLs decode far fewer times.
  while (depth < 50 && /(%2F|\/)identity(%2F|\/)login/i.test(value)) {
    depth += 1;
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch {
      break;
    }
  }
  return depth;
}

export async function getServerSideProps({
  req,
  res,
  query,
}: GetServerSidePropsContext) {
  // Run the popup flow only when actually rendered inside the Zendesk embed
  // iframe — not when /login loads top-level (the auth popup itself lands back
  // here on the same embedded host). Combine two server-side signals:
  //   - the embedded host (x-side-origin), the same check next-auth uses; and
  //   - Sec-Fetch-Dest: iframe, set by the browser only for framed document
  //     loads (top-level windows send `document`).
  const isEmbeddedHost = getIsEmbedded(req.headers['x-side-origin']);
  const isFramed = req.headers['sec-fetch-dest'] === 'iframe';
  const embedded = isEmbeddedHost && isFramed;

  const loginNestingDepth = getLoginNestingDepth(query.destination);
  const rawDestination = Array.isArray(query.destination)
    ? query.destination[0]
    : query.destination;

  // Self-heal a looping URL before doing anything else. A destination that
  // nests /identity/login is the signature of the AuthGate redirect loop
  // (incidents #4122 and #4220): each cycle re-wraps the full current URL as
  // the new destination, so the URL grows without bound and every hop counts
  // against monitors. AuthGate lives in @side/next-auth and still builds that
  // destination uncapped, so even with the crash fixed, any future /login
  // failure could restart the pump — normalizing here turns an incipient loop
  // into a single 302 to the unwrapped inner destination (depth 0 on the next
  // load, so this guard cannot itself loop).
  if (loginNestingDepth > 0) {
    const cleaned = sanitizeDestination(query.destination);
    embedDebugLog('WARNING', 'login destination loop detected — normalizing', {
      loginNestingDepth,
      cleanedDestination: cleaned ?? null,
      destinationLength: rawDestination?.length ?? 0,
      destinationPreview: rawDestination?.slice(0, 200) ?? null,
      embedded,
      referer: req.headers.referer ?? null,
      xSideOrigin: req.headers['x-side-origin'] ?? null,
      secFetchDest: req.headers['sec-fetch-dest'] ?? null,
    });
    return {
      redirect: {
        destination: cleaned
          ? `/login?destination=${encodeURIComponent(cleaned)}`
          : '/login',
        permanent: false,
      },
    };
  }

  // Only pay the flag lookup for embedded loads; normal logins are untouched.
  //
  // Resilience rules learned from two prod incidents (#4122, #4220):
  //   - req/res MUST be passed through — without them getServerSession()
  //     reaches for next/headers' headers(), which throws outside an App
  //     Router request scope and 500'd every embedded /login load; and
  //   - the lookup is wrapped so ANY flag-resolution failure degrades to the
  //     popup-off (= pre-embed) login instead of a 500. A 500 here is what
  //     feeds the destination-nesting redirect loop: the error page still
  //     mounts AuthGate, which redirects back to /login with the full current
  //     URL as the new destination.
  let popupAuthEnabled = false;
  let flagError: unknown;
  if (embedded) {
    try {
      popupAuthEnabled = await getServerFeatureFlag(
        'zendeskEmbedPopupAuth',
        undefined,
        { req, res },
      );
    } catch (err) {
      flagError = err;
    }
  }

  // [iap-embed-debug] Verbose on purpose: the embed flow spans Zendesk,
  // identity-ui, IAP, and admin-ui, and prior incidents were only diagnosable
  // from these logs. Searchable in Datadog via "iap-embed-debug". Keep until
  // the Zendesk embed is GA'd and stable.
  const logDetails: Record<string, unknown> = {
    embedded,
    isEmbeddedHost,
    isFramed,
    popupAuthEnabled,
    flagError:
      flagError instanceof Error
        ? { message: flagError.message, stack: flagError.stack }
        : (flagError ?? null),
    xSideOrigin: req.headers['x-side-origin'] ?? null,
    secFetchDest: req.headers['sec-fetch-dest'] ?? null,
    referer: req.headers.referer ?? null,
    host: req.headers.host ?? null,
    hasSessionCookie: Boolean(
      req.cookies['__Secure-next-auth.session-token'] ??
        req.cookies['next-auth.session-token'],
    ),
    urlLength: req.url?.length ?? 0,
    destinationLength: rawDestination?.length ?? 0,
    destinationPreview: rawDestination?.slice(0, 200) ?? null,
    sanitizedDestination: sanitizeDestination(query.destination) ?? null,
    loginNestingDepth,
  };
  embedDebugLog(
    flagError ? 'ERROR' : 'INFO',
    'login getServerSideProps',
    logDetails,
  );

  return {
    props: {
      embedded,
      popupAuthEnabled,
    },
  };
}

export default LoginPage;
